import { App as CapApp } from '@capacitor/app';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Plus,
  Sparkles,
  TrendingDown,
  TrendingUp,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { BottomSheet } from '../../components';
import { isNative } from '../../lib/platform';
import { useProContext } from '../../pro';
import { springQuick, tapScale } from '../../tokens';

type Action = {
  key: 'quick' | 'income' | 'expense';
  label: string;
  /** Pixels above the FAB the action button should rest at. */
  offset: number;
  Icon: LucideIcon;
  iconClass: string;
  bgClass: string;
};

const ACTIONS: Action[] = [
  {
    key: 'quick',
    label: 'Quick Add',
    offset: 68,
    Icon: Zap,
    iconClass: 'text-white',
    bgClass: 'bg-zinc-700 dark:bg-zinc-300 [&_svg]:dark:text-zinc-900',
  },
  {
    key: 'income',
    label: 'Add income',
    offset: 128,
    Icon: TrendingUp,
    iconClass: 'text-white',
    bgClass: 'bg-emerald-600',
  },
  {
    key: 'expense',
    label: 'Add expense',
    offset: 188,
    Icon: TrendingDown,
    iconClass: 'text-white',
    bgClass: 'bg-red-500',
  },
];

type Props = {
  /** Tapping "Quick Add" — opens the preset apply sheet (or Add modal if no presets). */
  onQuickAdd: () => void;
  onAddIncome: () => void;
  onAddExpense: () => void;
};

/**
 * Today-screen FAB cluster: an expanding Add Transaction action menu
 * (Quick Add / Add income / Add expense) with the AI assistant FAB
 * stacked above. The AI FAB hides while the action menu is open so the
 * fly-out has a clean visual hierarchy.
 */
export function TodayFabs({ onQuickAdd, onAddIncome, onAddExpense }: Props) {
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const pro = useProContext();

  // Hardware back collapses the menu before exiting the screen.
  useEffect(() => {
    if (!open || !isNative()) return;
    let handle: { remove: () => void } | undefined;
    void CapApp.addListener('backButton', () => {
      setOpen(false);
    }).then((h) => {
      handle = h;
    });
    return () => handle?.remove();
  }, [open]);

  const dispatch = (key: Action['key']) => {
    setOpen(false);
    // Defer the navigation a tick so the close animation can start
    // before a full-screen modal mounts on top.
    window.setTimeout(() => {
      if (key === 'quick') onQuickAdd();
      else if (key === 'income') onAddIncome();
      else onAddExpense();
    }, 80);
  };

  const tapAi = () => {
    if (pro.isPro) setAiOpen(true);
    else pro.openPaywall();
  };

  return (
    <>
      {/* Backdrop while menu is open */}
      <AnimatePresence>
        {open ? (
          <motion.button
            key="backdrop"
            type="button"
            aria-label="Close add menu"
            onClick={() => setOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-30 bg-black/30 dark:bg-black/50"
          />
        ) : null}
      </AnimatePresence>

      {/* Anchor at the FAB position; absolute children flow upward */}
      <div
        className="fixed right-5 z-40"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom) + 84px)',
        }}
      >
        {/* AI assistant FAB — stacked 16px above the Add FAB. Hidden when menu is open. */}
        <motion.button
          type="button"
          onClick={tapAi}
          aria-label="Ask Margin"
          whileTap={tapScale}
          transition={springQuick}
          animate={{
            opacity: open ? 0 : 1,
            scale: open ? 0.9 : 1,
          }}
          style={{
            pointerEvents: open ? 'none' : 'auto',
            position: 'absolute',
            right: 6,
            bottom: 72,
          }}
          className="w-11 h-11 rounded-full bg-zinc-100 dark:bg-zinc-800 inline-flex items-center justify-center shadow-md"
        >
          <Sparkles
            size={20}
            strokeWidth={1.75}
            className="text-blue-500 dark:text-blue-400"
          />
        </motion.button>

        {/* Action buttons fly out vertically */}
        <AnimatePresence>
          {open
            ? ACTIONS.map((a, i) => (
                <motion.div
                  key={a.key}
                  initial={{ opacity: 0, y: 10, x: 10 }}
                  animate={{ opacity: 1, y: 0, x: 0 }}
                  exit={{ opacity: 0, y: 10, x: 10 }}
                  transition={{
                    type: 'spring',
                    stiffness: 280,
                    damping: 26,
                    delay: i * 0.04,
                  }}
                  style={{
                    position: 'absolute',
                    right: 0,
                    bottom: a.offset,
                  }}
                  className="flex items-center gap-2"
                >
                  <button
                    type="button"
                    onClick={() => dispatch(a.key)}
                    aria-label={a.label}
                    className="rounded-full bg-surface px-3 py-1.5 text-[13px] font-medium text-text-primary shadow-md"
                  >
                    {a.label}
                  </button>
                  <motion.button
                    type="button"
                    onClick={() => dispatch(a.key)}
                    aria-label={a.label}
                    whileTap={tapScale}
                    transition={springQuick}
                    className={`w-12 h-12 rounded-full inline-flex items-center justify-center shadow-md ${a.bgClass}`}
                  >
                    <a.Icon
                      size={22}
                      strokeWidth={1.75}
                      className={a.iconClass}
                    />
                  </motion.button>
                </motion.div>
              ))
            : null}
        </AnimatePresence>

        {/* Primary FAB — plus rotates to X when open */}
        <motion.button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Close add menu' : 'Add transaction menu'}
          whileTap={tapScale}
          transition={springQuick}
          className="w-14 h-14 rounded-full bg-accent text-white inline-flex items-center justify-center shadow-lg shadow-blue-500/30"
        >
          <motion.span
            animate={{ rotate: open ? 45 : 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 22 }}
            className="inline-flex"
          >
            {open ? (
              <X size={24} strokeWidth={2} />
            ) : (
              <Plus size={24} strokeWidth={2} />
            )}
          </motion.span>
        </motion.button>
      </div>

      <BottomSheet
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        title="Ask Margin"
      >
        <div className="px-screen-x py-6 flex flex-col items-center text-center gap-3">
          <div className="w-12 h-12 rounded-full bg-accent/10 inline-flex items-center justify-center text-accent">
            <Sparkles size={22} strokeWidth={1.5} />
          </div>
          <h3 className="text-[18px] font-semibold text-text-primary">
            AI Assistant
          </h3>
          <p className="text-[14px] text-text-secondary max-w-[280px]">
            Ask Margin anything about your money: categorize a transaction,
            forecast a goal, explain a number. Coming soon.
          </p>
        </div>
      </BottomSheet>
    </>
  );
}
